import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Heart, MessageCircle, Share2, TrendingUp, Clock, Calendar, RotateCw, ExternalLink, Radio } from 'lucide-react';
import PlayableVideoThumbnail from '../components/ui/PlayableVideoThumbnail';
import Nav from '../components/Nav';
import LoadingSpinner from '../components/LoadingSpinner';
import { API_BASE, ROUTES } from '../../shared/constants';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';

// Chart & UI components
import ChartCard from '../components/ui/ChartCard';
import BaseChart from '../components/ui/BaseChart';
import { formatLargeNumber, normalizeValue, generatePolylinePoints, getTooltipTransform } from '../components/ui/chartUtils';
import ReportTab from './video-detail/ReportTab';

interface VideoData {
  video: {
    id: string;
    tiktok_id?: string;
    title: string;
    cover_image_url?: string;
    posted_at: string;
    created_at: string;
    view_count: number;
    like_count: number;
    comment_count: number;
    share_count: number;
    engagement_rate: number;
    duration?: number;
    last_synced_at?: string;
  };
  account: {
    username: string;
    name: string;
    avatar_url?: string;
  } | null;
  snapshots: {
    views: Array<{ timestamp: string; value: number }>;
    likes: Array<{ timestamp: string; value: number }>;
    comments: Array<{ timestamp: string; value: number }>;
    shares: Array<{ timestamp: string; value: number }>;
    engagement: Array<{ timestamp: string; value: number }>;
  };
  accountAverages: {
    totalVideos: number;
    avgViews: number;
    avgLikes: number;
    avgComments: number;
    avgShares: number;
    avgEngagement: number;
  } | null;
  accountAverages48h: {
    totalVideos: number;
    avgViews: number;
    avgLikes: number;
    avgComments: number;
    avgShares: number;
    avgEngagement: number;
  } | null;
}


const VIEW_MODE_STORAGE_KEY = 'adam-video-detail-view-mode';
type ViewMode = '48h' | 'lifetime';
type DashboardTab = 'default' | 'advanced' | 'report';
/*
export function calculateVelocity(snapshots: Array<{ timestamp: string; value: number }>): Array<{ timestamp: string; value: number }> {
  if (snapshots.length < 2) return [];
  
  const velocity: Array<{ timestamp: string; value: number }> = [];
  
  for (let i = 1; i < snapshots.length; i++) {
    const prev = snapshots[i - 1];
    const curr = snapshots[i];
    const deltaValue = curr.value - prev.value;
    const deltaTimeHours = (new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime()) / (1000 * 60 * 60);
    const velocityValue = deltaTimeHours > 0 ? deltaValue / deltaTimeHours : 0;
    
    velocity.push({
      timestamp: curr.timestamp,
      value: Math.round(Math.max(0, velocityValue))
    });
  }
  
  return velocity;
}
*/

// Helper: Calculate engagement ratios (percentage of views)
/*
export function calculateEngagementRatios(
  views: Array<{ timestamp: string; value: number }>,
  engagement: Array<{ timestamp: string; value: number }>
): Array<{ timestamp: string; value: number }> {
  return views.map((v, i) => ({
    timestamp: v.timestamp,
    value: v.value > 0 ? (engagement[i]?.value || 0) / v.value * 100 : 0
  }));
}
*/

// Helper: Filter data to first N hours
function filterFirstNHours(
  snapshots: Array<{ timestamp: string; value: number }>,
  postedAt: string,
  hours: number
): Array<{ timestamp: string; value: number }> {
  const postTime = new Date(postedAt).getTime();
  const cutoff = postTime + (hours * 60 * 60 * 1000);
  
  return snapshots.filter(s => new Date(s.timestamp).getTime() <= cutoff);
}

// Helper: Generate adaptive X-axis labels based on time span
function generateAdaptiveXLabels(
  data: Array<{ timestamp: string }>,
  count = 5
): Array<{ index: number; text: string }> {
  if (data.length === 0) return [];

  const firstTime = new Date(data[0].timestamp).getTime();
  const lastTime = new Date(data[data.length - 1].timestamp).getTime();
  const spanMs = lastTime - firstTime;
  const spanHours = spanMs / (1000 * 60 * 60);

  // Choose format based on time span
  const formatLabel = (ts: string): string => {
    const d = new Date(ts);
    if (spanHours < 1) {
      // Under 1 hour: show minutes  "12:05 PM"
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (spanHours < 24) {
      // Under 24 hours: show hours  "2 PM" or "2:30 PM"
      const mins = d.getMinutes();
      if (mins === 0) return d.toLocaleTimeString([], { hour: 'numeric' });
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (spanHours < 72) {
      // 1-3 days: show day + hour  "Feb 8, 2 PM"
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
        ', ' + d.toLocaleTimeString([], { hour: 'numeric' });
    } else {
      // 3+ days: show just dates  "Feb 8"
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Deduplicate: if adjacent labels would be identical, skip them
  const rawIndices = Array.from({ length: count }, (_, i) =>
    i === count - 1 ? data.length - 1 : Math.floor((data.length - 1) * i / (count - 1))
  );

  const labels: Array<{ index: number; text: string }> = [];
  let lastText = '';
  for (const idx of rawIndices) {
    const text = formatLabel(data[idx].timestamp);
    if (text !== lastText) {
      labels.push({ index: idx, text });
      lastText = text;
    }
  }
  return labels;
}

/**
 * Video Detail Page - Individual video performance analytics
 * Shows time-series charts for all video metrics
 */
export default function VideoDetailPage() {
  const { username, videoId } = useParams<{ username: string; videoId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [data, setData] = useState<VideoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      return stored === 'lifetime' ? 'lifetime' : '48h';
    } catch { return '48h'; }
  });
  const [activeTab, setActiveTab] = useState<DashboardTab>('report');

  const handleModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode); } catch {}
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/analytics/video/${videoId}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to load video data');
        }

        const videoData = await response.json();
        setData(videoData);
      } catch (err) {
        console.error('Error fetching video data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load video data');
      } finally {
        setIsLoading(false);
      }
    };

    if (videoId) {
      fetchVideoData();
    }
  }, [videoId]);

  const handleBack = () => {
    // Navigate to the video owner's dashboard (prefer account username from API)
    const ownerUsername = data?.account?.username || username;
    navigate(`/dashboard/${ownerUsername}`);
  };

  // Video age calculation (safe even when data is null)
  const video = data?.video;
  const snapshots = data?.snapshots;
  const videoAgeHours = video ? (Date.now() - new Date(video.posted_at).getTime()) / (1000 * 60 * 60) : 0;
  const isYoungerThan48h = !video || videoAgeHours < 48;
  const effectiveMode: ViewMode = isYoungerThan48h ? '48h' : viewMode;

  // Pre-filter snapshots for 48h mode (computed once, used by all charts)
  const filteredSnapshots = useMemo(() => {
    if (!snapshots || !video) return { views: [], likes: [], comments: [], shares: [], engagement: [] };
    if (effectiveMode === 'lifetime') return snapshots;
    return {
      views: filterFirstNHours(snapshots.views, video.posted_at, 48),
      likes: filterFirstNHours(snapshots.likes, video.posted_at, 48),
      comments: filterFirstNHours(snapshots.comments, video.posted_at, 48),
      shares: filterFirstNHours(snapshots.shares, video.posted_at, 48),
      engagement: filterFirstNHours(snapshots.engagement, video.posted_at, 48),
    };
  }, [snapshots, video?.posted_at, effectiveMode]);



  // Active snapshots for current mode
  const activeSnapshots = filteredSnapshots;

  // --- TOP 10 ANALYTICS CALCULATION ---
  const analytics = useMemo(() => {
    if (!activeSnapshots.views.length) return null;

    const len = activeSnapshots.views.length; 
    // Initialize arrays
    const deltaV: any[] = [];
    const deltaL: any[] = [];
    const deltaC: any[] = [];
    const deltaS: any[] = [];
    
    const velV: any[] = [];
    const velL: any[] = [];
    const velC: any[] = [];
    const velS: any[] = [];
    
    const accV: any[] = [];
    const normVelV: any[] = [];
    
    const cumRateL: any[] = [];
    const cumRateC: any[] = [];
    const cumRateS: any[] = [];
    
    const margRateL: any[] = [];
    const margRateC: any[] = [];
    const margRateS: any[] = [];
    
    const weightScore: any[] = [];
    const shareToLike: any[] = [];
    const commentToLike: any[] = [];

    for (let i = 0; i < len; i++) {
        // Safe access to parallel arrays
        const v = activeSnapshots.views[i];
        const l = activeSnapshots.likes[i] || { value: 0 };
        const c = activeSnapshots.comments[i] || { value: 0 };
        const s = activeSnapshots.shares[i] || { value: 0 };
        
        if (!v) continue;
        const ts = v.timestamp;
        
        // 6. Cumulative Rates (%)
        const safeV = Math.max(1, v.value);
        cumRateL.push({ timestamp: ts, value: (l.value / safeV) * 100 });
        cumRateC.push({ timestamp: ts, value: (c.value / safeV) * 100 });
        cumRateS.push({ timestamp: ts, value: (s.value / safeV) * 100 });
        
        // 9 & 10. Ratios
        const safeL = Math.max(1, l.value);
        shareToLike.push({ timestamp: ts, value: s.value / safeL });
        commentToLike.push({ timestamp: ts, value: c.value / safeL });
        
        // 8. Weighted Engagement Efficiency (Score per View)
        // Weight: Like=1, Comment=3, Share=5
        const score = l.value + (3 * c.value) + (5 * s.value);
        weightScore.push({ timestamp: ts, value: score / safeV });

        // Derivatives (requiring previous point)
        if (i > 0) {
            const prevV = activeSnapshots.views[i-1];
            const prevL = activeSnapshots.likes[i-1] || { value: 0 };
            const prevC = activeSnapshots.comments[i-1] || { value: 0 };
            const prevS = activeSnapshots.shares[i-1] || { value: 0 };

            const t1 = new Date(ts).getTime();
            const t0 = new Date(prevV.timestamp).getTime();
            const dtHours = (t1 - t0) / (1000 * 3600);
            
            if (dtHours > 0.01) { // Avoid div by zero
                 // 1. Growth (Deltas) - Raw count added since last snapshot
                 const dV = Math.max(0, v.value - prevV.value);
                 const dL = Math.max(0, l.value - prevL.value);
                 const dC = Math.max(0, c.value - prevC.value);
                 const dS = Math.max(0, s.value - prevS.value);
                 
                 deltaV.push({ timestamp: ts, value: dV });
                 deltaL.push({ timestamp: ts, value: dL });
                 deltaC.push({ timestamp: ts, value: dC });
                 deltaS.push({ timestamp: ts, value: dS });
                 
                 // 2 & 3. Velocities (Per Hour)
                 const vV = dV / dtHours;
                 const vL = dL / dtHours;
                 const vC = dC / dtHours;
                 const vS = dS / dtHours;
                 
                 velV.push({ timestamp: ts, value: vV });
                 velL.push({ timestamp: ts, value: vL });
                 velC.push({ timestamp: ts, value: vC });
                 velS.push({ timestamp: ts, value: vS });
                 
                 // 4. Acceleration (Change in View Velocity / hr)
                 if (velV.length > 1) {
                     const prevVel = velV[velV.length - 2].value;
                     accV.push({ timestamp: ts, value: (vV - prevVel) / dtHours });
                 } else {
                     accV.push({ timestamp: ts, value: 0 });
                 }
                 
                 // 5. Normalized Velocity (Growth relative to current size)
                 // "How fast is it growing compared to how big it is?"
                 normVelV.push({ timestamp: ts, value: (vV / safeV) * 100 });
                 
                 // 7. Marginal Rates (Delta Engagement / Delta Views) (%)
                 // "For every new 100 views, how many likes/shares?"
                 const safeDV = Math.max(1, dV);
                 margRateL.push({ timestamp: ts, value: (dL / safeDV) * 100 });
                 margRateC.push({ timestamp: ts, value: (dC / safeDV) * 100 });
                 margRateS.push({ timestamp: ts, value: (dS / safeDV) * 100 });
            }
        }
    }
    
    return {
       deltaV, deltaL, deltaC, deltaS,
       velV, velL, velC, velS,
       accV,
       normVelV,
       cumRateL, cumRateC, cumRateS,
       margRateL, margRateC, margRateS,
       weightScore,
       shareToLike, commentToLike
    };

  }, [activeSnapshots]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Nav email={user?.email} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error || !data || !video || !snapshots) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Nav email={user?.email} onLogout={handleLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error || 'Video not found'}</p>
            <button
              onClick={handleBack}
              className="text-primary-400 hover:text-primary-300 transition"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper: format date like "Mon 12, Jan 2026, 2:30 PM"
  const formatPostedDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[d.getDay()];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${dayName} ${day}, ${month} ${year}, ${hour12}:${minutes} ${ampm}`;
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Nav email={user?.email} onLogout={handleLogout} />
      
      <div className="max-w-7xl mx-auto px-1 md:px-4 py-8 md:pt-20">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Dashboard</span>
        </button>

        {/* Video Summary Overview — always visible */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 md:p-6 mb-4">
          <div className="flex flex-row gap-4 md:gap-6">
            {/* Thumbnail */}
            {video.cover_image_url && (
              <PlayableVideoThumbnail
                coverImageUrl={video.cover_image_url}
                title={video.title}
                tiktokId={video.tiktok_id}
                username={data?.account?.username || username}
                videoId={video.id}
                className="w-1/3 md:w-48"
              />
            )}

            {/* Right column: metadata + stats */}
            <div className="flex-1 flex flex-col justify-center min-w-0">
              {/* Title row */}
              <div className="flex items-start gap-2 mb-2">
                <h1 className="text-base md:text-2xl font-bold text-white flex-1 min-w-0 line-clamp-3" title={video.title}>
                  {video.title}
                </h1>
                {video.tiktok_id && (
                  <a href={`https://www.tiktok.com/@${data?.account?.username || username}/video/${video.tiktok_id}`} target="_blank" rel="noopener noreferrer"
                     className="text-slate-400 hover:text-sky-400 transition-colors flex-shrink-0" title="Open on TikTok">
                    <ExternalLink className="w-4 h-4 md:w-5 md:h-5" />
                  </a>
                )}
                <span className="flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] md:text-xs font-medium border border-emerald-500/20 flex-shrink-0">
                  <Radio className="w-3 h-3 mr-1" /> Live
                </span>
              </div>

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] md:text-xs text-slate-400 mb-3">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span>{video.posted_at ? formatPostedDate(video.posted_at) : 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  <span>{formatDuration(video.duration || 0)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <RotateCw className="w-3.5 h-3.5 text-slate-500" />
                  <span>Synced: {video.last_synced_at ? new Date(video.last_synced_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}</span>
                </div>
              </div>

              {/* Desktop: Metric tiles — 5 tiles including Engagement */}
              <div className="hidden md:grid grid-cols-5 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 text-center mb-1">Views</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold text-white">{(video.view_count || 0).toLocaleString()}</span>
                    <Eye className="w-4 h-4 text-sky-400 flex-shrink-0" />
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 text-center mb-1">Likes</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold text-white">{(video.like_count || 0).toLocaleString()}</span>
                    <Heart className="w-4 h-4 text-red-500 flex-shrink-0" />
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 text-center mb-1">Comments</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold text-white">{(video.comment_count || 0).toLocaleString()}</span>
                    <MessageCircle className="w-4 h-4 text-violet-400 flex-shrink-0" />
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 text-center mb-1">Shares</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold text-white">{(video.share_count || 0).toLocaleString()}</span>
                    <Share2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 text-center mb-1">Engagement</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold" style={{ color: '#fbbf24' }}>{video.engagement_rate?.toFixed(2) || '0.00'}%</span>
                    <TrendingUp className="w-4 h-4 flex-shrink-0" style={{ color: '#fbbf24' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Metric tiles as row below — 5 tiles */}
          <div className="grid md:hidden grid-cols-5 gap-1.5 mt-4">
            <div className="bg-slate-800/50 rounded-lg p-1.5">
              <div className="text-[8px] text-slate-500 text-center">Views</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs font-bold text-white">{(video.view_count || 0).toLocaleString()}</span>
                <Eye className="w-3 h-3 text-sky-400 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-1.5">
              <div className="text-[8px] text-slate-500 text-center">Likes</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs font-bold text-white">{(video.like_count || 0).toLocaleString()}</span>
                <Heart className="w-3 h-3 text-red-500 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-1.5">
              <div className="text-[8px] text-slate-500 text-center">Comments</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs font-bold text-white">{(video.comment_count || 0).toLocaleString()}</span>
                <MessageCircle className="w-3 h-3 text-violet-400 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-1.5">
              <div className="text-[8px] text-slate-500 text-center">Shares</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs font-bold text-white">{(video.share_count || 0).toLocaleString()}</span>
                <Share2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-1.5">
              <div className="text-[8px] text-slate-500 text-center">Eng.</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xs font-bold" style={{ color: '#fbbf24' }}>{video.engagement_rate?.toFixed(2) || '0'}%</span>
                <TrendingUp className="w-3 h-3 flex-shrink-0" style={{ color: '#fbbf24' }} />
              </div>
            </div>
          </div>
        </div>

        {/* View Controls & Toggles */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 bg-slate-900/40 p-4 rounded-xl border border-slate-800/50 backdrop-blur-sm">
          
          {/* Tab Selector: Default / Advanced */}
          <div className="flex items-center gap-2 p-1 bg-slate-950/50 rounded-lg border border-slate-800/50">
            <button
               onClick={() => setActiveTab('report')}
               className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                 activeTab === 'report'
                   ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                   : 'text-slate-400 hover:text-slate-200'
               }`}
            >
              Report
            </button>
            <button
               onClick={() => setActiveTab('advanced')}
               className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                 activeTab === 'advanced'
                   ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                   : 'text-slate-400 hover:text-slate-200'
               }`}
            >
              Advanced
            </button>
          </div>

          {/* Left: Time Window Control */}
          {activeTab === 'advanced' && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-300">
              <span className="text-sm font-semibold text-slate-400">Time Range:</span>
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-1 flex">
                <button
                  onClick={() => handleModeChange('48h')}
                  disabled={isYoungerThan48h}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    effectiveMode === '48h'
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30 shadow-[0_0_10px_-3px_rgba(14,165,233,0.3)]'
                      : 'text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    First 48h
                  </span>
                </button>
                <button
                  onClick={() => handleModeChange('lifetime')}
                  disabled={isYoungerThan48h}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    effectiveMode === 'lifetime'
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'text-slate-400 hover:text-slate-300'
                  } ${isYoungerThan48h ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  Lifetime
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Charts Grid - Adaptive Layout */}
        <div className="mt-8">

      {activeTab === 'report' && (
        <ReportTab 
          data={activeSnapshots} 
          video={video} 
          account={data?.account} 
          username={username} 
        />
      )}

      {activeTab === 'advanced' && (
          /* === ADVANCED VIEW: TOP 10 ANALYTICS === */
          <div className="space-y-12 animate-in fade-in duration-700">
              
              {/* Info Banner */}
              <div className="flex items-start gap-3 px-1 opacity-80">
                <div className="mt-0.5 min-w-[4px] h-4 rounded-full bg-primary-500/50"></div>
                <p className="text-xs text-slate-400 leading-relaxed max-w-3xl">
                   {effectiveMode === '48h' 
                     ? 'Analyzing the first 48 hours with high-resolution derivatives.'
                     : 'Showing lifetime analytics. Switch to "First 48h" for sensitive velocity metrics.'
                   }
                </p>
              </div>

              {analytics && (
                <>
                  {/* 1. Interval Growth (Deltas) */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
                       <h2 className="text-xl font-bold text-white">1. Interval Growth</h2>
                       <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">Raw New Volume</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <VideoChart
                        title="View Growth (Delta V)"
                        subtitle="New views per snapshot interval"
                        data={analytics.deltaV}
                        color="#38bdf8"
                        metricName="New Views"
                      />
                      <MultiLineChart
                        title="Engagement Growth"
                        subtitle="New Likes, Comments, Shares per interval"
                        series={[
                          { data: analytics.deltaL, color: '#ef4444', label: 'New Likes' },
                          { data: analytics.deltaC, color: '#8b5cf6', label: 'New Comments' },
                          { data: analytics.deltaS, color: '#10b981', label: 'New Shares' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* 2 & 3. Velocities */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
                       <h2 className="text-xl font-bold text-white">2 & 3. Velocity (Speed)</h2>
                       <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">Normalized to Per Hour</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <VideoChart
                        title="View Velocity"
                        subtitle="Views per Hour"
                        data={analytics.velV}
                        color="#38bdf8"
                        metricName="Views/hr"
                      />
                      <MultiLineChart
                        title="Engagement Velocities"
                        subtitle="Actions per Hour"
                        series={[
                          { data: analytics.velL, color: '#ef4444', label: 'Likes/hr' },
                          { data: analytics.velC, color: '#8b5cf6', label: 'Comments/hr' },
                          { data: analytics.velS, color: '#10b981', label: 'Shares/hr' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* 4 & 5. Acceleration & Normalized Velocity */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
                       <h2 className="text-xl font-bold text-white">4 & 5. Momentum Dynamics</h2>
                       <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">Force & Scale</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <VideoChart
                        title="View Acceleration"
                        subtitle="Change in View Velocity per Hour"
                        data={analytics.accV}
                        color="#f59e0b"
                        metricName="Acc (v/h²)"
                      />
                      <VideoChart
                        title="Normalized Velocity"
                        subtitle="Speed relative to current size (% growth/hr)"
                        data={analytics.normVelV}
                        color="#ec4899"
                        metricName="% Growth/hr"
                      />
                    </div>
                  </div>

                  {/* 6 & 7. Cumulative vs Marginal Rates */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
                       <h2 className="text-xl font-bold text-white">6 & 7. Engagement Quality</h2>
                       <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">Overall vs Recent</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <MultiLineChart
                        title="Cumulative Engagement Rates"
                        subtitle="Total Actions / Total Views (%)"
                        series={[
                          { data: analytics.cumRateL, color: '#ef4444', label: 'Like Rate %' },
                          { data: analytics.cumRateC, color: '#8b5cf6', label: 'Comment Rate %' },
                          { data: analytics.cumRateS, color: '#10b981', label: 'Share Rate %' },
                        ]}
                      />
                      <MultiLineChart
                        title="Marginal Engagement Rates"
                        subtitle="New Actions / New Views (Recent Quality)"
                        series={[
                          { data: analytics.margRateL, color: '#ef4444', label: 'Marginal Like %' },
                          { data: analytics.margRateC, color: '#8b5cf6', label: 'Marginal Comment %' },
                          { data: analytics.margRateS, color: '#10b981', label: 'Marginal Share %' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* 8, 9, 10. Composite Scores & Ratios */}
                  <div className="space-y-4">
                     <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
                       <h2 className="text-xl font-bold text-white">8, 9, 10. Deep Signals</h2>
                       <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">Efficiency & Psychology</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                       <VideoChart
                        title="8. Weighted Efficiency"
                        subtitle="Comp. Score per View: 1*L + 3*C + 5*S"
                        data={analytics.weightScore}
                        color="#6366f1"
                        metricName="Score/View"
                      />
                      <VideoChart
                        title="9. Share-to-Like Ratio"
                        subtitle="Viral Stickiness (>0.1 is strong)"
                        data={analytics.shareToLike}
                        color="#10b981"
                        metricName="Share/Like"
                      />
                      <VideoChart
                        title="10. Comment-to-Like Ratio"
                        subtitle="Controversy / Discussion Level"
                        data={analytics.commentToLike}
                        color="#8b5cf6"
                        metricName="Comm/Like"
                      />
                    </div>
                  </div>

                </>
              )}
            </div>
          )}

          {activeTab === 'default' && (
            <div className="space-y-8 animate-in fade-in duration-500 pb-12">
                
                {/* 1. HERO STATS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-800 backdrop-blur-sm hover:bg-slate-900/70 transition-colors group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                      <Eye className="w-16 h-16 text-sky-400 transform rotate-12 translate-x-4 -translate-y-4" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-sky-500/10 rounded-lg">
                          <Eye className="w-5 h-5 text-sky-400" />
                        </div>
                        <h3 className="text-sm font-medium text-slate-400">Total Views</h3>
                      </div>
                      <div className="text-3xl font-bold text-white tracking-tight mb-2">
                        {video.view_count.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          data.accountAverages && video.view_count > data.accountAverages.avgViews
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-slate-700/50 text-slate-400 border border-slate-600/50'
                        }`}>
                          {data.accountAverages?.avgViews ? (
                            <>
                              {video.view_count > data.accountAverages.avgViews ? 'Above' : 'Below'} Average
                            </>
                          ) : 'Tracked'}
                        </span>
                      </div>
                    </div>
                  </div>

                   <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-800 backdrop-blur-sm hover:bg-slate-900/70 transition-colors group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                      <TrendingUp className="w-16 h-16 text-emerald-400 transform rotate-12 translate-x-4 -translate-y-4" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-emerald-500/10 rounded-lg">
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h3 className="text-sm font-medium text-slate-400">Engagement Rate</h3>
                      </div>
                      <div className="text-3xl font-bold text-white tracking-tight mb-2">
                        {video.engagement_rate.toFixed(1)}%
                      </div>
                       <div className="flex items-center gap-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          Healthy
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-800 backdrop-blur-sm hover:bg-slate-900/70 transition-colors group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                      <Share2 className="w-16 h-16 text-purple-400 transform rotate-12 translate-x-4 -translate-y-4" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-purple-500/10 rounded-lg">
                          <Share2 className="w-5 h-5 text-purple-400" />
                        </div>
                        <h3 className="text-sm font-medium text-slate-400">Total Shares</h3>
                      </div>
                      <div className="text-3xl font-bold text-white tracking-tight mb-2">
                        {video.share_count.toLocaleString()}
                      </div>
                       <div className="flex items-center gap-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                           High Vitality
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. GHOST RACER (Simplified Pacing) */}
                <div className="grid grid-cols-1 gap-6">
                  {/* Performance Comparison */}
                  <PerformanceComparisonChart
                      title="Ghost Racer"
                      subtitle="Your performance vs. account average"
                      currentVideo={video}
                      accountAverages={data.accountAverages}
                  />
                </div>

                {/* 3. AI COACH */}
                <div className="bg-slate-900/50 p-6 rounded-xl border border-indigo-500/20 backdrop-blur-sm bg-gradient-to-br from-indigo-900/10 to-transparent shadow-lg shadow-indigo-900/10">
                   <h3 className="text-lg font-bold text-indigo-200 mb-4 flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></div>
                     Action Plan
                   </h3>
                   <div className="space-y-4">
                     <div className="bg-slate-950/80 p-4 rounded-xl border border-indigo-500/20 flex gap-4 hover:border-indigo-500/40 transition-colors">
                        <div className="shrink-0 w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-300 flex items-center justify-center font-bold text-sm border border-indigo-500/30">1</div>
                        <div>
                           <p className="text-sm font-semibold text-slate-100 mb-1">Reply to comments now</p>
                           <p className="text-xs text-slate-400 leading-relaxed">Engagement velocity is slowing down. Early replies can boost algorithmic reach by +15%.</p>
                        </div>
                     </div>
                     <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 flex gap-4 opacity-75 hover:opacity-100 transition-opacity">
                        <div className="shrink-0 w-8 h-8 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center font-bold text-sm border border-slate-700">2</div>
                        <div>
                           <p className="text-sm font-semibold text-slate-200 mb-1">Check retention at 0:03</p>
                           <p className="text-xs text-slate-400 leading-relaxed">Drop-off is slightly higher than your account average for this hook structure.</p>
                        </div>
                     </div>
                   </div>
                </div>

             </div>
          )}

        </div>

      </div>
    </div>
  );
}

export interface VideoChartProps {
  title: string;
  subtitle?: string;
  data: Array<{ timestamp: string; value: number }>;
  color: string;
  metricName: string;
  isPercentage?: boolean;
  icon?: React.ReactNode;
}

export function VideoChart({ title, subtitle, data, color, metricName, isPercentage = false, icon }: VideoChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} isLoading={false}>
        <div className="h-48 flex items-center justify-center text-slate-500">
          No data available
        </div>
      </ChartCard>
    );
  }

  const dataPoints = data.map(d => d.value);
  const maxVal = Math.max(...dataPoints, 0);
  const minVal = Math.min(...dataPoints, 0);

  const width = 1000;
  const height = 200;
  const topPadding = 20;
  const bottomPadding = 20;

  const normalizeY = (val: number) => 
    normalizeValue(val, minVal, maxVal, height, topPadding, bottomPadding);

  const points = generatePolylinePoints(dataPoints, width, normalizeY);

  // Generate X-axis labels (adaptive to time span)
  const xAxisLabels = generateAdaptiveXLabels(data);

  // Generate Y-axis labels - 5 labels to match 5 horizontal grid lines
  const yAxisLabels = [
    { value: maxVal, text: isPercentage ? `${maxVal.toFixed(1)}%` : formatLargeNumber(Math.round(maxVal)) },
    { value: maxVal * 0.75 + minVal * 0.25, text: isPercentage ? `${(maxVal * 0.75 + minVal * 0.25).toFixed(1)}%` : formatLargeNumber(Math.round(maxVal * 0.75 + minVal * 0.25)) },
    { value: (maxVal + minVal) / 2, text: isPercentage ? `${((maxVal + minVal) / 2).toFixed(1)}%` : formatLargeNumber(Math.round((maxVal + minVal) / 2)) },
    { value: maxVal * 0.25 + minVal * 0.75, text: isPercentage ? `${(maxVal * 0.25 + minVal * 0.75).toFixed(1)}%` : formatLargeNumber(Math.round(maxVal * 0.25 + minVal * 0.75)) },
    { value: minVal, text: isPercentage ? `${minVal.toFixed(1)}%` : formatLargeNumber(Math.round(minVal)) }
  ];

  const handleChartInteraction = (clientX: number, rect: DOMRect) => {
    const relativeX = (clientX - rect.left) / rect.width;
    const closestIndex = Math.round(relativeX * (dataPoints.length - 1));
    setHoveredPoint(Math.max(0, Math.min(dataPoints.length - 1, closestIndex)));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    handleChartInteraction(e.clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    handleChartInteraction(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    handleChartInteraction(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchEnd = () => {
    setHoveredPoint(null);
  };

  const legend = (
    <div className="flex items-center gap-1.5">
      <div className="w-3 h-0.5" style={{ backgroundColor: color }}></div>
      {icon || <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{metricName}</span>}
    </div>
  );

  const growth = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] - dataPoints[0] : 0;
  const actions = (
    <div className="text-right">
      <span className={`text-2xl font-bold ${growth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
        {growth >= 0 ? '+' : ''}{isPercentage ? growth.toFixed(2) + '%' : growth.toLocaleString()}
      </span>
      <p className="text-xs text-slate-400 uppercase tracking-wide">{metricName}</p>
    </div>
  );

  const overlays = (
    <>
      {/* Hover dot */}
      {hoveredPoint !== null && (
        <div className="absolute inset-0 pointer-events-none z-20">
          <div
            className="absolute rounded-full border-2 border-white shadow-sm"
            style={{
              backgroundColor: color,
              left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
              top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
              width: '12px',
              height: '12px',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </div>
      )}

      {/* Tooltip */}
      {hoveredPoint !== null && (
        <div
          className="absolute bg-slate-800 text-white text-xs px-2 py-1.5 rounded border border-slate-700 shadow-2xl pointer-events-none z-40"
          style={{
            left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
            top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
            transform: getTooltipTransform(hoveredPoint, dataPoints.length, '-45px')
          }}
        >
          <div className="font-bold">
            {isPercentage ? dataPoints[hoveredPoint].toFixed(2) + '%' : dataPoints[hoveredPoint].toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400">
            {new Date(data[hoveredPoint].timestamp).toLocaleDateString([], { 
              month: 'short', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      )}
    </>
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legend={legend}
      actions={actions}
      isLoading={false}
    >
      <BaseChart
        width={width}
        height={height}
        topPadding={topPadding}
        bottomPadding={bottomPadding}
        horizontalGridCount={5}
        dataLength={data.length}
        xAxisLabels={xAxisLabels}
        yAxisLabels={yAxisLabels}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        overlays={overlays}
      >
        {/* Main line */}
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="3"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </BaseChart>
    </ChartCard>
  );
}

/* ─── Chart 2: Multi-Line Chart (Engagement Breakdown) ─── */

interface SeriesConfig {
  data: Array<{ timestamp: string; value: number }>;
  color: string;
  label: string;
  dashPattern?: string;
}

interface MultiLineChartProps {
  title: string;
  subtitle?: string;
  series: SeriesConfig[];
  isPercentage?: boolean;
}

function MultiLineChart({ title, subtitle, series, isPercentage = false }: MultiLineChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  const validSeries = series.filter(s => s.data?.length > 0);
  if (validSeries.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} isLoading={false}>
        <div className="h-48 flex items-center justify-center text-slate-500">No data available</div>
      </ChartCard>
    );
  }

  // First series (Likes) uses left Y-axis, remaining (Comments, Shares) use right Y-axis
  const leftSeries = validSeries[0];
  const rightSeries = validSeries.slice(1);
  const refData = leftSeries.data;

  // Left axis scale (Likes)
  const lVals = leftSeries.data.map(d => d.value);
  const lMax = Math.max(...lVals, 0);
  const lMin = Math.min(...lVals, 0);

  // Right axis scale (Comments + Shares — shared scale)
  const rAllVals = rightSeries.flatMap(s => s.data.map(d => d.value));
  const rMax = Math.max(...rAllVals, 0);
  const rMin = Math.min(...rAllVals, 0);

  const width = 1000;
  const height = 200;
  const topPadding = 20;
  const bottomPadding = 20;

  const normL = (v: number) => normalizeValue(v, lMin, lMax, height, topPadding, bottomPadding);
  const normR = (v: number) => normalizeValue(v, rMin, rMax, height, topPadding, bottomPadding);

  // Build polyline points
  const leftPoints = generatePolylinePoints(lVals, width, normL);
  const rightSeriesPoints = rightSeries.map(s => {
    const vals = s.data.map(d => d.value);
    return {
      ...s,
      values: vals,
      points: generatePolylinePoints(vals, width, normR),
    };
  });

  const xAxisLabels = generateAdaptiveXLabels(refData);

  // Left Y-axis labels (Likes) — colored to match
  const yAxisLabels = [
    { value: lMax, text: isPercentage ? `${lMax.toFixed(1)}%` : formatLargeNumber(lMax) },
    { value: lMax * 0.75 + lMin * 0.25, text: isPercentage ? `${(lMax * 0.75 + lMin * 0.25).toFixed(1)}%` : formatLargeNumber(lMax * 0.75 + lMin * 0.25) },
    { value: (lMax + lMin) / 2, text: isPercentage ? `${((lMax + lMin) / 2).toFixed(1)}%` : formatLargeNumber((lMax + lMin) / 2) },
    { value: lMax * 0.25 + lMin * 0.75, text: isPercentage ? `${(lMax * 0.25 + lMin * 0.75).toFixed(1)}%` : formatLargeNumber(lMax * 0.25 + lMin * 0.75) },
    { value: lMin, text: isPercentage ? `${lMin.toFixed(1)}%` : formatLargeNumber(lMin) }
  ];

  // Right Y-axis labels (Like Rate scale) — integers only
  const rightAxisColor = rightSeries.length > 1 ? rightSeries[rightSeries.length - 1].color : (rightSeries.length > 0 ? rightSeries[0].color : '#94a3b8');
  const rightYAxisLabels = [
    { value: rMax, text: isPercentage ? `${rMax.toFixed(1)}%` : formatLargeNumber(Math.round(rMax)), color: rightAxisColor },
    { value: rMax * 0.75 + rMin * 0.25, text: isPercentage ? `${(rMax * 0.75 + rMin * 0.25).toFixed(1)}%` : formatLargeNumber(Math.round(rMax * 0.75 + rMin * 0.25)), color: rightAxisColor },
    { value: (rMax + rMin) / 2, text: isPercentage ? `${((rMax + rMin) / 2).toFixed(1)}%` : formatLargeNumber(Math.round((rMax + rMin) / 2)), color: rightAxisColor },
    { value: rMax * 0.25 + rMin * 0.75, text: isPercentage ? `${(rMax * 0.25 + rMin * 0.75).toFixed(1)}%` : formatLargeNumber(Math.round(rMax * 0.25 + rMin * 0.75)), color: rightAxisColor },
    { value: rMin, text: isPercentage ? `${rMin.toFixed(1)}%` : formatLargeNumber(Math.round(rMin)), color: rightAxisColor }
  ];

  const handleChartInteraction = (clientX: number, rect: DOMRect) => {
    const relativeX = (clientX - rect.left) / rect.width;
    const idx = Math.round(relativeX * (refData.length - 1));
    setHoveredPoint(Math.max(0, Math.min(refData.length - 1, idx)));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    handleChartInteraction(e.clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    handleChartInteraction(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    handleChartInteraction(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchEnd = () => {
    setHoveredPoint(null);
  };

  const labelIconMap: Record<string, React.ReactNode> = {
    Likes: <Heart className="w-3.5 h-3.5" />,
    Comments: <MessageCircle className="w-3.5 h-3.5" />,
    Shares: <Share2 className="w-3.5 h-3.5" />,
  };

  const legend = (
    <div className="flex items-center gap-4">
      {validSeries.map(s => (
        <div key={s.label} className="flex items-center gap-1.5">
          <div className="w-3 h-0.5" style={{ backgroundColor: s.color, ...(s.dashPattern ? { backgroundImage: `repeating-linear-gradient(90deg, ${s.color} 0px, ${s.color} 4px, transparent 4px, transparent 7px)`, backgroundColor: 'transparent' } : {}) }}></div>
          <span style={{ color: s.color }}>{labelIconMap[s.label] || s.label}</span>
        </div>
      ))}
    </div>
  );

  const overlays = hoveredPoint !== null ? (
    <>
      {/* Left series hover dot (Likes) */}
      <div className="absolute inset-0 pointer-events-none z-20">
        <div className="absolute rounded-full border-2 border-white shadow-sm" style={{
          backgroundColor: leftSeries.color,
          left: `${(hoveredPoint / (lVals.length - 1)) * 100}%`,
          top: `${(normL(lVals[hoveredPoint]) / height) * 100}%`,
          width: '10px', height: '10px', transform: 'translate(-50%, -50%)'
        }} />
      </div>
      {/* Right series hover dots (Comments, Shares) */}
      {rightSeriesPoints.map(s => (
        <div key={s.label} className="absolute inset-0 pointer-events-none z-20">
          <div className="absolute rounded-full border-2 border-white shadow-sm" style={{
            backgroundColor: s.color,
            left: `${(hoveredPoint / (s.values.length - 1)) * 100}%`,
            top: `${(normR(s.values[hoveredPoint]) / height) * 100}%`,
            width: '10px', height: '10px', transform: 'translate(-50%, -50%)'
          }} />
        </div>
      ))}
      {/* Tooltip */}
      <div className="absolute bg-slate-800 text-white text-xs px-3 py-2 rounded border border-slate-700 shadow-2xl pointer-events-none z-40"
        style={{
          left: `${(hoveredPoint / (refData.length - 1)) * 100}%`,
          top: '10%',
          transform: getTooltipTransform(hoveredPoint, refData.length, '0px')
        }}
      >
        <div className="font-bold flex items-center gap-1" style={{ color: leftSeries.color }}>
          {isPercentage ? `${lVals[hoveredPoint]?.toFixed(2)}%` : lVals[hoveredPoint]?.toLocaleString()}
          {(leftSeries.label === 'Likes' || leftSeries.label === 'Like Rate') && <Heart className="w-3 h-3" />}
          {(leftSeries.label === 'Comments' || leftSeries.label === 'Comment Rate') && <MessageCircle className="w-3 h-3" />}
          {(leftSeries.label === 'Shares' || leftSeries.label === 'Share Rate') && <Share2 className="w-3 h-3" />}
        </div>
        {rightSeriesPoints.map(s => (
          <div key={s.label} className="font-bold flex items-center gap-1" style={{ color: s.color }}>
            {isPercentage ? `${s.values[hoveredPoint]?.toFixed(2)}%` : s.values[hoveredPoint]?.toLocaleString()}
            {(s.label === 'Likes' || s.label === 'Like Rate') && <Heart className="w-3 h-3" />}
            {(s.label === 'Comments' || s.label === 'Comment Rate') && <MessageCircle className="w-3 h-3" />}
            {(s.label === 'Shares' || s.label === 'Share Rate') && <Share2 className="w-3 h-3" />}
          </div>
        ))}
        <div className="text-[10px] text-slate-400 mt-1">
          {new Date(refData[hoveredPoint].timestamp).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </>
  ) : null;

  return (
    <ChartCard title={title} legend={legend} isLoading={false}>
      <BaseChart
        width={width} height={height}
        topPadding={topPadding} bottomPadding={bottomPadding}
        horizontalGridCount={5}
        dataLength={refData.length}
        xAxisLabels={xAxisLabels}
        yAxisLabels={yAxisLabels}
        rightYAxisLabels={rightYAxisLabels}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        overlays={overlays}
      >
        {/* Render in z-order: shares (bottom) → comments (middle) → likes (top) */}
        {/* Right-axis lines (Comments, Shares) — rendered first so they appear behind */}
        {[...rightSeriesPoints].reverse().map(s => (
          <polyline key={s.label} fill="none" stroke={s.color} strokeWidth="2.5" points={s.points} vectorEffect="non-scaling-stroke" {...(s.dashPattern ? { strokeDasharray: s.dashPattern } : {})} />
        ))}
        {/* Left-axis line (Likes) — rendered last so it appears on top */}
        <polyline fill="none" stroke={leftSeries.color} strokeWidth="2.5" points={leftPoints} vectorEffect="non-scaling-stroke" {...(leftSeries.dashPattern ? { strokeDasharray: leftSeries.dashPattern } : {})} />
      </BaseChart>
    </ChartCard>
  );
}

/* ─── NEW Chart: Performance Comparison ─── */

interface PerformanceComparisonChartProps {
  title: string;
  subtitle?: string;
  currentVideo: {
    id: string;
    view_count: number;
    like_count: number;
    comment_count: number;
    share_count: number;
    engagement_rate: number;
  };
  accountAverages: {
    totalVideos: number;
    avgViews: number;
    avgLikes: number;
    avgComments: number;
    avgShares: number;
    avgEngagement: number;
  } | null;
}

function PerformanceComparisonChart({ title, subtitle, currentVideo, accountAverages }: PerformanceComparisonChartProps) {
  if (!accountAverages || accountAverages.totalVideos === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} isLoading={false}>
        <div className="h-48 flex items-center justify-center text-slate-500">
          No videos available for comparison
        </div>
      </ChartCard>
    );
  }

  const metrics = [
    { label: 'Views', current: currentVideo.view_count, average: accountAverages.avgViews, color: '#38bdf8', icon: Eye },
    { label: 'Likes', current: currentVideo.like_count, average: accountAverages.avgLikes, color: '#ef4444', icon: Heart },
    { label: 'Comments', current: currentVideo.comment_count, average: accountAverages.avgComments, color: '#8b5cf6', icon: MessageCircle },
    { label: 'Shares', current: currentVideo.share_count, average: accountAverages.avgShares, color: '#10b981', icon: Share2 },
    { label: 'Engagement', current: currentVideo.engagement_rate, average: accountAverages.avgEngagement, color: '#fbbf24', icon: TrendingUp, isPercentage: true },
  ];

  const legend = (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22d3ee' }}></div>
        <span className="text-xs text-slate-400">This Video</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded bg-slate-600"></div>
        <span className="text-xs text-slate-400">Mean ({accountAverages.totalVideos} videos)</span>
      </div>
    </div>
  );

  return (
    <ChartCard title={title} subtitle={subtitle} legend={legend} isLoading={false}>
      <div className="space-y-4 p-2">
        {metrics.map((metric) => {
          const percentage = metric.average > 0 ? ((metric.current - metric.average) / metric.average) * 100 : 0;
          const isAboveAverage = metric.current >= metric.average;
          const Icon = metric.icon;
          const maxValue = Math.max(metric.current, metric.average, 1);
          const currentWidth = (metric.current / maxValue) * 100;
          const avgWidth = (metric.average / maxValue) * 100;

          return (
            <div key={metric.label}>
              {/* Metric header row */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: metric.color }} />
                  <span className="text-xs font-semibold text-slate-300">{metric.label}</span>
                </div>
                <div className={`text-xs font-bold px-1.5 py-0.5 rounded ${isAboveAverage ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                  {isAboveAverage ? '▲' : '▼'} {Math.abs(percentage).toFixed(0)}%
                </div>
              </div>

              {/* This Video bar */}
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 h-5 bg-slate-800/80 rounded overflow-hidden relative">
                  <div
                    className="h-full rounded transition-all duration-500"
                    style={{ width: `${currentWidth}%`, backgroundColor: metric.color, opacity: 0.85 }}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white/90">
                    {metric.isPercentage ? `${metric.current.toFixed(1)}%` : formatLargeNumber(Math.round(metric.current))}
                  </span>
                </div>
              </div>

              {/* Average bar */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-3 bg-slate-800/80 rounded overflow-hidden relative">
                  <div
                    className="h-full rounded transition-all duration-500 bg-slate-600"
                    style={{ width: `${avgWidth}%` }}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-medium text-slate-400">
                    {metric.isPercentage ? `${metric.average.toFixed(1)}%` : formatLargeNumber(Math.round(metric.average))}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}


